import React, { useEffect, useState, useCallback } from "react";
import {
  AnalyticalTable,
  Button,
  Dialog,
  Label,
  Title,
  Select,
  Option,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Icon,
  TextArea,
} from "@ui5/webcomponents-react";
import axiosInstance from "../utils/axiosInstance";
import { parseToNumber, formatNumberWithComma, rightAlignedNumberStyle } from '../utils/validator';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const CenterList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    sortBy: "code",
    sortDirection: "asc",
  });
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    address: "",
    city: "",
    stateProvince: "",
    country: "Chile",
    postalCode: "",
    latitude: 0,
    longitude: 0,
    totalAreaSqm: 0,
    totalCapacityCubicMeter: 0,
    maxHeightMeter: 0,
    isActive: true,
    contactPerson: "",
    contactEmail: "",
    contactPhone: "",
    securityLevel: "medio",
  });
  const [formErrors, setFormErrors] = useState({});

  const fetchCenters = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const url = `centers?page=${page}&size=${pageSize}`;
        const response = await axiosInstance.get(url);
        if (response.data.records.code === "SUCCESS") {
          setData(response.data.records.data);
          setTotalPages(Math.ceil(response.data.totalCount / pageSize));
        } else {
          setError(response.data.records.message);
        }
      } catch (err) {
        setError(
          "Error al cargar los centros: " + (err.message || "Error desconocido")
        );
      } finally {
        setLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    fetchCenters(currentPage);
  }, [currentPage, fetchCenters]);

  useEffect(() => {
    let filtered = [...data];

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (center) =>
          center.code?.toLowerCase().includes(lowerCaseSearchTerm) ||
          center.name?.toLowerCase().includes(lowerCaseSearchTerm) ||
          center.city?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    filtered.sort((a, b) => {
      const valueA = a[sortConfig.sortBy];
      const valueB = b[sortConfig.sortBy];

      if (typeof valueA === "string" && typeof valueB === "string") {
        return sortConfig.sortDirection === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      } else {
        return sortConfig.sortDirection === "asc"
          ? valueA - valueB
          : valueB - valueA;
      }
    });

    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (column) => {
    setSortConfig((prevConfig) => ({
      sortBy: column,
      sortDirection:
        prevConfig.sortBy === column && prevConfig.sortDirection === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleRowClick = (event) => {
    if (!hasPermission('centers', 'update')) {
      return;
    }
    
    const { detail } = event;
    if (detail && detail.row && detail.row.original) {
      const center = detail.row.original;
      setSelectedCenter(center);
      setFormData({
        code: center.code || "",
        name: center.name || "",
        address: center.address || "",
        city: center.city || "",
        stateProvince: center.stateProvince || "",
        country: center.country || "Chile",
        postalCode: center.postalCode || "",
        latitude: center.latitude || 0,
        longitude: center.longitude || 0,
        totalAreaSqm: center.totalAreaSqm || 0,
        totalCapacityCubicMeter: center.totalCapacityCubicMeter || 0,
        maxHeightMeter: center.maxHeightMeter || 0,
        isActive: center.isActive !== undefined ? center.isActive : true,
        contactPerson: center.contactPerson || "",
        contactEmail: center.contactEmail || "",
        contactPhone: center.contactPhone || "",
        securityLevel: center.securityLevel || "medio",
      });
      setShowEditDialog(true);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleCreateClick = () => {
    if (!hasPermission('centers', 'create')) {
      return;
    }
    
    setFormData({
      code: "",
      name: "",
      address: "",
      city: "",
      stateProvince: "",
      country: "Chile",
      postalCode: "",
      latitude: 0,
      longitude: 0,
      totalAreaSqm: 0,
      totalCapacityCubicMeter: 0,
      maxHeightMeter: 0,
      isActive: true,
      contactPerson: "",
      contactEmail: "",
      contactPhone: "",
      securityLevel: "medio",
    });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleEditClick = (center) => {
    if (!hasPermission('centers', 'update')) {
      return;
    }
    
    setSelectedCenter(center);
    setFormData({
      code: center.code || "",
      name: center.name || "",
      address: center.address || "",
      city: center.city || "",
      stateProvince: center.stateProvince || "",
      country: center.country || "Chile",
      postalCode: center.postalCode || "",
      latitude: center.latitude || 0,
      longitude: center.longitude || 0,
      totalAreaSqm: center.totalAreaSqm || 0,
      totalCapacityCubicMeter: center.totalCapacityCubicMeter || 0,
      maxHeightMeter: center.maxHeightMeter || 0,
      isActive: center.isActive !== undefined ? center.isActive : true,
      contactPerson: center.contactPerson || "",
      contactEmail: center.contactEmail || "",
      contactPhone: center.contactPhone || "",
      securityLevel: center.securityLevel || "medio",
    });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleDeleteClick = (center) => {
    if (!hasPermission('centers', 'delete')) {
      return;
    }
    
    setSelectedCenter(center);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCenter) return;

    try {
      await axiosInstance.delete(`centers/${selectedCenter.id}`);
      fetchCenters(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError(
        "Error al eliminar el centro: " + (err.message || "Error desconocido")
      );
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };


  const handleNumberInputChange = (event) => {
    const { name, value } = event.target;
      setFormData({
        ...formData,
        [name]: value
      });
    if (formErrors[name]) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSelectChange = (event) => {
    const { name } = event.target;
    const value = event.detail.selectedOption.getAttribute("value");

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleToggleActive = () => {
    setFormData((prev) => ({
      ...prev,
      isActive: !prev.isActive,
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.code) {
      errors.code = "El código es obligatorio";
    }
    if (!formData.name) {
      errors.name = "El nombre es obligatorio";
    }
    if (!formData.address) {
      errors.address = "La dirección es obligatoria";
    }
    if (!formData.city) {
      errors.city = "La ciudad es obligatoria";
    }
    if (!formData.stateProvince) {
      errors.stateProvince = "La región es obligatoria";
    }
    if (!formData.contactPerson) {
      errors.contactPerson = "La persona de contacto es obligatoria";
    }
    if (!formData.contactEmail) {
      errors.contactEmail = "El email de contacto es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
      errors.contactEmail = "El email no es válido";
    }
    if (!formData.contactPhone) {
      errors.contactPhone = "El teléfono de contacto es obligatorio";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        ...formData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const response = await axiosInstance.post("centers", payload);
      if (response.status === 201 || response.status === 200) {
        fetchCenters(currentPage);
        setShowCreateDialog(false);
      } else {
        setError(
          "Error al crear el centro: " +
            (response.data.message || "Error desconocido")
        );
      }
    } catch (err) {
      setError(
        "Error al crear el centro: " + (err.message || "Error desconocido")
      );
    }
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedCenter) return;

    try {
      const payload = {
        ...formData,
        id: selectedCenter.id,
        updatedAt: new Date().toISOString(),
      };

      const response = await axiosInstance.put(
        `centers/${selectedCenter.id}`,
        payload
      );
      if (response.status === 200) {
        fetchCenters(currentPage);
        setShowEditDialog(false);
      } else {
        setError(
          "Error al actualizar el centro: " +
            (response.data.message || "Error desconocido")
        );
      }
    } catch (err) {
      setError(
        "Error al actualizar el centro: " + (err.message || "Error desconocido")
      );
    }
  };

  const columns = [
    {
      Header: "Código",
      accessor: "code",
      headerProps: {
        onClick: () => handleSort("code"),
      },
      className:
        sortConfig.sortBy === "code"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Nombre",
      accessor: "name",
      headerProps: {
        onClick: () => handleSort("name"),
      },
      className:
        sortConfig.sortBy === "name"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Ciudad",
      accessor: "city",
      headerProps: {
        onClick: () => handleSort("city"),
      },
      className:
        sortConfig.sortBy === "city"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Región",
      accessor: "stateProvince",
      headerProps: {
        onClick: () => handleSort("stateProvince"),
      },
      className:
        sortConfig.sortBy === "stateProvince"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Área Total (m²)",
      accessor: "totalAreaSqm",
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>
          {formatNumberWithComma(value?.toLocaleString()) || "0"}
        </Text>
      ),
      headerProps: {
        onClick: () => handleSort("totalAreaSqm"),
      },
      className:
        sortConfig.sortBy === "totalAreaSqm"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Capacidad (m³)",
      accessor: "totalCapacityCubicMeter",
      Cell: ({ value }) => (
        <Text style={rightAlignedNumberStyle()}>
          {formatNumberWithComma(value?.toLocaleString()) || "0"}
        </Text>
      ),
      headerProps: {
        onClick: () => handleSort("totalCapacityCubicMeter"),
      },
      className:
        sortConfig.sortBy === "totalCapacityCubicMeter"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Estado",
      accessor: "isActive",
      Cell: ({ value }) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Icon
            name={value ? "status-positive" : "status-negative"}
            style={{ marginRight: "8px", color: value ? "#28a745" : "#dc3545" }}
          />
          <Text>{value ? "Activo" : "Inactivo"}</Text>
        </div>
      ),
      headerProps: {
        onClick: () => handleSort("isActive"),
      },
      className:
        sortConfig.sortBy === "isActive"
          ? `sorted-${sortConfig.sortDirection}`
          : "",
    },
    {
      Header: "Acciones",
      id: "actions",
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "8px" }}>
          {hasPermission('centers', 'update') && (
            <Button
              icon="edit"
              design="Transparent"
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(row.original);
              }}
            />
          )}
          {hasPermission('centers', 'delete') && (
            <Button
              icon="delete"
              design="Transparent"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
            />
          )}
        </div>
      ),
    },
  ];

  const securityLevels = [
    { value: "bajo", label: "Bajo" },
    { value: "medio", label: "Medio" },
    { value: "alto", label: "Alto" },
  ];

  return (
    <>
      <div style={{ padding: "1rem" }}>
        <Title level="H2">{autoT('Gestión de centros logísticos')}</Title>

        {error && <div style={{ color: "red", margin: "1rem 0" }}>{error}</div>}

        <Bar
          startContent={
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <Input
                placeholder={autoT('Buscar centro...')}
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "250px" }}
              />
            </div>
          }
          endContent={
            hasPermission('centers', 'create') && (
              <Button icon="add" design="Emphasized" onClick={handleCreateClick}>
                {autoT('Nuevo Centro')}
              </Button>
            )
          }
        />

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "2rem",
            }}
          >
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('centers', 'update') ? handleRowClick : undefined}
              style={{ marginTop: "1rem" }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              <Button
                icon="navigation-left-arrow"
                disabled={currentPage === 1}
                onClick={handlePrevPage}
              >
                Anterior
              </Button>
              <Text style={{ alignSelf: "center" }}>
                Página {currentPage} de {totalPages}
              </Text>
              <Button
                icon="navigation-right-arrow"
                disabled={currentPage === totalPages}
                onClick={handleNextPage}
                iconEnd
              >
                Siguiente
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Dialog para crear/editar centro */}
      <Dialog
        open={showCreateDialog || showEditDialog}
        onClose={() =>
          showCreateDialog
            ? setShowCreateDialog(false)
            : setShowEditDialog(false)
        }
        header={
          <Bar
            startContent={
              <Title level="H4">
                {showCreateDialog 
                  ? autoT('Crear Nuevo Centro') 
                  : `${autoT('Editar Centro')}: ${selectedCenter?.name}`}
              </Title>
            }
          />
        }
        footer={
          <Bar
            startContent={
              hasPermission('centers', 'delete') && (
                <Button design="Negative" onClick={handleDeleteClick} icon="delete">
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button
                  design="Emphasized"
                  onClick={showCreateDialog ? handleCreateSubmit : handleEditSubmit}
                >
                  {showCreateDialog ? autoT('Crear') : autoT('Guardar')}
                </Button>
                <Button
                  design="Transparent"
                  onClick={() =>
                    showCreateDialog
                      ? setShowCreateDialog(false)
                      : setShowEditDialog(false)
                  }
                >
                  {autoT('Cancelar')}
                </Button>
              </>
            }
          />
        }
        style={{ width: '1200px' }}
      >
        <Form 
          style={{ padding: '1rem' }}
          titleText="Información del Centro"
          backgroundDesign="Transparent"
          columnsXL={3}
          columnsL={3}
          columnsM={2}
          columnsS={1}
          labelSpanXL={4}
          labelSpanL={4}
          labelSpanM={12}
          labelSpanS={12}
        >
          {formErrors.general && (
            <FormItem>
              <Text style={{ color: 'var(--sapErrorColor)', marginBottom: '1rem' }}>{formErrors.general}</Text>
            </FormItem>
          )}

          <FormGroup titleText="Información Básica">
            <FormItem labelContent={<Label>Código</Label>}>
              <Input
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                valueState={formErrors.code ? "Error" : "None"}
                valueStateMessage={formErrors.code}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Nombre</Label>}>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                valueState={formErrors.name ? "Error" : "None"}
                valueStateMessage={formErrors.name}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Estado</Label>}>
              <Button
                icon={formData.isActive ? "status-positive" : "status-negative"}
                design="Transparent"
                onClick={handleToggleActive}
              >
                {formData.isActive ? "Activo" : "Inactivo"}
              </Button>
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Ubicación">
            <FormItem labelContent={<Label>Dirección</Label>}>
              <Input
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                valueState={formErrors.address ? "Error" : "None"}
                valueStateMessage={formErrors.address}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Ciudad</Label>}>
              <Input
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                valueState={formErrors.city ? "Error" : "None"}
                valueStateMessage={formErrors.city}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Región</Label>}>
              <Input
                name="stateProvince"
                value={formData.stateProvince}
                onChange={handleInputChange}
                valueState={formErrors.stateProvince ? "Error" : "None"}
                valueStateMessage={formErrors.stateProvince}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>País</Label>}>
              <Input
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>Código Postal</Label>}>
              <Input
                name="postalCode"
                value={formData.postalCode}
                onChange={handleInputChange}
              />
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Coordenadas">
            <FormItem labelContent={<Label>Latitud</Label>}>
              <Input
                type="text"
                name="latitude"
                value={formatNumberWithComma(formData.latitude)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>Longitud</Label>}>
              <Input
                type="text"
                name="longitude"
                value={formatNumberWithComma(formData.longitude)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Características Físicas">
            <FormItem labelContent={<Label>Área Total (m²)</Label>}>
              <Input
                type="text"
                name="totalAreaSqm"
                value={formatNumberWithComma(formData.totalAreaSqm)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>Capacidad Total (m³)</Label>}>
              <Input
                type="text"
                name="totalCapacityCubicMeter"
                value={formatNumberWithComma(formData.totalCapacityCubicMeter)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
            <FormItem labelContent={<Label>Altura Máxima (m)</Label>}>
              <Input
                type="text"
                name="maxHeightMeter"
                value={formatNumberWithComma(formData.maxHeightMeter)}
                onChange={handleNumberInputChange}
              />
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Información de contacto">
            <FormItem labelContent={<Label>Persona de contacto</Label>}>
              <Input
                name="contactPerson"
                value={formData.contactPerson}
                onChange={handleInputChange}
                valueState={formErrors.contactPerson ? "Error" : "None"}
                valueStateMessage={formErrors.contactPerson}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Email de contacto</Label>}>
              <Input
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                valueState={formErrors.contactEmail ? "Error" : "None"}
                valueStateMessage={formErrors.contactEmail}
                required
              />
            </FormItem>
            <FormItem labelContent={<Label>Teléfono de contacto</Label>}>
              <Input
                name="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                valueState={formErrors.contactPhone ? "Error" : "None"}
                valueStateMessage={formErrors.contactPhone}
                required
              />
            </FormItem>
          </FormGroup>

          <FormGroup titleText="Seguridad">
            <FormItem labelContent={<Label>Nivel de seguridad</Label>}>
              <Select
                name="securityLevel"
                onChange={handleSelectChange}
                value={formData.securityLevel}
              >
                {securityLevels.map((level) => (
                  <Option key={level.value} value={level.value}>
                    {level.label}
                  </Option>
                ))}
              </Select>
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación de eliminación */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar el centro')} <strong>{selectedCenter?.name}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </>
  );
};

export default withPermission(CenterList, 'centers', 'listView');
